
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  toRef,
  watch,
} from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import { FormApi } from "@/services/api/Form.service";
import { ICurrentUser } from "@/interfaces/iCurrentUser";
import { SiteApi } from "@/services/api/Site.service";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { hasPermission } from "@/core/services/permission.service";
import { hideModal } from "@/core/helpers/dom";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { useI18n } from "vue-i18n/index";
import AddEditModal from "@/components/modals/forms/AddEditModal.vue";
import moment from "moment";
import { useStore } from "vuex";
import { getLookups } from "@/services/api/Lookup.service";
import { ScheduleApi } from "@/services/api/Schedule.service";
import { useToast } from "vue-toastification";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";
import { MESSAGE_STORE, SMS_DATA_SELECTED_ID } from "@/store/message/types";
import { Actions } from "@/store/enums/StoreEnums";
import { SmsAPI } from "@/services/api/Sms.service";
import { UserAPI } from "@/services/api/User.service";
export default defineComponent({
  name: "add-schedule-modal",
  emit: ["submit", "cancel", "task-delete"],
  props: {
    data: { type: Object, default: null },
    modelValue: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
    VZDeleteModal,
    AddEditModal,
  },
  setup(props, { emit }) {
    const { t, te } = useI18n();
    const translate = (key: string) => {
      if (te(key)) {
        return t(key);
      } else {
        return key.split(".").join(" ");
      }
    };
    const toast = useToast();
    const sites = ref<any>({});
    const forms = ref<any>([]);
    const users = ref<any>({});

    const participants = ref<number[]>([]);
    const participantsOptions = ref<any>([]);
    const linkConversationsOptions = ref<any>([]);
    const linkConversationsList = ref<any>([]);
    const formName = ref();
    const conversations = ref<any>();
    let modalTitle = ref<any>(translate("common.addTask"));
    let editing = ref<boolean>(false);
    let addConvDialog = ref<boolean>(false);
    let linkConvDialog = ref<boolean>(false);
    const showDeleteDialog = ref<boolean>(false);
    let targetData = ref<any>({});
    let participantsIds = ref<any>([]);
    let selectedSite = ref<any>();
    const task = ref<any>();
    const scheduleData = toRef(props, "data");
    const noteData = ref<string>();
    const modelValueProp = toRef(props, "modelValue");
    const editable = toRef(props, "isEditable");
    const documentId = ref<any>();
    const selectedNote = ref<any>();
    const showDialog = ref<boolean>(false);
    const showNotesDetail = ref<boolean>(false);
    const scheduleModalRef = ref<null | HTMLElement>();
    const detailTab = ref<HTMLElement>();
    const noteTab = ref<HTMLElement>();
    const conversationTab = ref<HTMLElement>();
    const scheduleFormRef = ref<null | HTMLFormElement>();
    const store = useStore();
    const selectedConversation = ref<any>([]);
    const noteFormRef = ref<null | HTMLFormElement>();
    const closeButtonRef = ref<null | HTMLButtonElement>();
    const noteCloseButtonRef = ref<null | HTMLButtonElement>();
    const formDisabled = ref<boolean>(false);
    const assignType = ref<any>("");
    const documentAssignmentModels = ref<any>();
    const notes = ref<any>([]);
    const conversationsArray = ref<any>([]);
    const linkedConversationsArray = ref<any>([]);
    const conversationsToDelete = ref<any>([]);
    const initialNotes = ref<any>([]);
    const userList = ref<any>([]);
    const toggleChatDrawer = ref<null | HTMLElement>();
    const eventDenyMsg = t("pages.scheduler.cannotCreateTaskForDate");
    const conversationCreated = translate(
      "pages.scheduler.conversationCreated"
    );
    const conversationEdited = translate("pages.scheduler.conversationEdited");
    const conversationDeleteMessage = translate(
      "pages.scheduler.conversationDeleted"
    );
    const conversationAlreadyExist = translate(
      "pages.scheduler.convoAlreadyExists"
    );
    const noteDeletedMsg = t("pages.scheduler.noteDeleted");
    const lessThanThirtyMints = t("validation.lessThanThirtyMints");
    const currentUser = computed(() => {
      return store.getters.currentUser as ICurrentUser;
    });
    let scheduleFormData = ref({
      validationSchema: Yup.object({
        site: Yup.mixed().required(
          t("common.site") + " " + t("common.isRequired")
        ),
        user: Yup.mixed().required(
          t("common.user") +
            "/" +
            t("common.role") +
            " " +
            t("common.isRequired")
        ),
        form: Yup.mixed().required(
          t("common.form") + " " + t("common.isRequired")
        ),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .nullable()
          .required(t("common.endDate") + " " + t("common.isRequired"))
          .min(Yup.ref("startDate"), t("validation.greaterEndDate")),
      }),
    });
    let noteValidationSchema = Yup.object({
      note: Yup.string()
        .strict(true)
        .max(500, getMaxLengthErrorMessage(t("common.note"), 500))
        .required(t("common.note") + " " + t("common.isRequired"))
        .matches(/\S/, t("common.note") + " " + t("common.isRequired")),
    });
    const loading = ref<boolean>(false);
    const displayDateTime = computed(() => {
      return process.env.VUE_APP_DISPLAY_TIME || "MM-DD-YYYY HH:mm";
    });
    onMounted(async () => {
      conversations.value = [];
      await SiteApi.getInstance()
        .getSiteList({
          size: 100000,
          status: "ACTIVE",
        })
        .then((data) => {
          sites.value = data.content;
          sites.value.sort((a, b) => (a.name > b.name ? 1 : -1));
        })
        .catch((err) => {
          console.error(err);
        });
      await getLookups(`security/filter/USERS`)
        .then((data: any) => {
          data.map((obj: any) => {
            participantsOptions.value.push({
              label: obj.text,
              value: obj.value,
            });
          });
        })
        .catch((err) => {
          return [];
        });
      getLikedConversations();
    });

    const getLikedConversations = async () => {
      await SmsAPI.getInstance()
        .getSmsList({
          status: "OPEN",
        })
        .then((data) => {
          linkConversationsList.value = [];
          linkConversationsOptions.value = [];
          linkConversationsList.value = data.content;
          data.content.map((conversations) => {
            linkConversationsOptions.value.push({
              label: conversations.title,
              value: conversations.webId,
            });
          });
        })
        .catch((err) => {
          console.error(err);
        });
    };
    const deleteLinkedTask = async () => {
      await SmsAPI.getInstance()
        .bulkDeleteConversations(conversationsToDelete.value)
        .then((data) => {
          //
        })
        .catch((err) => {
          console.error(err);
        });
    };
    const deleteNote = async (note) => {
      if (note.noteId != null) {
        await FormApi.getInstance()
          .deleteNote(note.noteId)
          .then((res) => {
            toast.success(noteDeletedMsg);
          })
          .catch((err) => {
            console.error(err);
          });
      }
      notes.value.splice(notes.value.indexOf(note), 1);
    };
    const deleteConversation = async (conv, index) => {
      formData.value.edit = false;
      if (!conv.webId) {
        conversations.value.splice(index, 1);
        const existingIndex = conversationsArray.value.findIndex(
          (existingConversation) => existingConversation.localId == conv.localId
        );
        if (existingIndex > -1) {
          conversationsArray.value.splice(existingIndex, 1);
          toast.success(conversationDeleteMessage);
        }
      } else {
        conversations.value.splice(index, 1);
        if (!conv.linked) {
          conversationsToDelete.value.push({
            conversationId: conv.conversationDocumentModels[0].conversationId,
            documentId: conv.conversationDocumentModels[0].documentId,
          });
          const existingIndex = conversationsArray.value.findIndex(
            (existingConversation) => existingConversation.webId == conv.webId
          );
          if (existingIndex > -1) {
            conversationsArray.value.splice(existingIndex, 1);
            toast.success(conversationDeleteMessage);
            return;
          }
        } else {
          if (conv.conversationDocumentModels) {
            conversationsToDelete.value.push({
              conversationId: conv.conversationDocumentModels[0].conversationId,
              documentId: conv.conversationDocumentModels[0].documentId,
            });
            linkedConversationsArray.value;
            const existingIndex = linkedConversationsArray.value.findIndex(
              (existingConversation) => existingConversation.webId == conv.webId
            );
            if (existingIndex > -1) {
              linkedConversationsArray.value.splice(existingIndex, 1);
              toast.success(conversationDeleteMessage);
              return;
            }
          } else {
            const existingIndex = linkedConversationsArray.value.findIndex(
              (existingConversation) => existingConversation.webId == conv.webId
            );
            if (existingIndex > -1) {
              linkedConversationsArray.value.splice(existingIndex, 1);
              toast.success(conversationDeleteMessage);
              return;
            }
          }
        }
        // conversationsArray.value = [];
        // await ScheduleApi.getInstance()
        //   .deleteConversation(conv.webId)
        //   .then((res) => {
        //     toast.success("Conversation deleted successfully");
        //     getConversations();
        //   });
      }
    };

    let myVariable: string | number = "";
    const toggleChat = async (id) => {
      toggleChatDrawer.value?.click();
      if (typeof myVariable === "string") {
        myVariable = id;
        store.dispatch(`${MESSAGE_STORE}/${SMS_DATA_SELECTED_ID}`, id);
      } else {
        myVariable = id.toString();
        store.dispatch(
          `${MESSAGE_STORE}/${SMS_DATA_SELECTED_ID}`,
          id.toString()
        );
      }
      const element = document.querySelector(".el-dialog") as HTMLElement;
      if (element) {
        element.setAttribute("tabindex", "");
      }
      const ele = document.getElementById(
        "kt_drawer_chat_toggle"
      ) as HTMLElement;
      if (ele) {
        ele.click();
      }
    };

    let linkFormData = ref<IAddEditFormModel>({
      header: "Link Chat",
      edit: false,
      data: null,
      overrideLable: true,
      label: [{ editHeader: "Link Chat" }],
      validationSchema: Yup.object({
        conversationId: Yup.number()
          .nullable()
          .required(t("common.conversation") + " " + t("common.isRequired")),
      }),
      model: [
        {
          name: "conversationId",
          label: t("common.conversation"),
          placeholder: t("common.conversation"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          options: linkConversationsOptions,
        },
      ],
    });
    let formData = ref<IAddEditFormModel>({
      header: "Chat",
      edit: false,
      data: null,
      label: [{ editHeader: "Add Chat" }],
      validationSchema: Yup.object({
        title: Yup.string()
          .strict(true)
          .max(30, getMaxLengthErrorMessage(t("common.name"), 30))
          .required(t("common.name") + " " + t("common.isRequired"))
          .matches(/\S/, t("common.name") + " " + t("common.isRequired")),
        participants: Yup.array()
          .nullable()
          .of(Yup.number().required())
          .min(1, t("common.participants") + " " + "are required")
          .required(t("common.participants") + " " + "are required"),
      }),
      model: [
        {
          name: "title",
          label: t("common.name"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
        {
          name: "participants",
          label: t("common.participants"),
          placeholder: t("pages.user.selectUser"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          multiSelect: true,
          options: participantsOptions,
        },
      ],
    });
    const handleDelete = () => {
      showDeleteDialog.value = true;
    };

    const siteUpdated = (val) => {
      selectedSite.value = val;
      targetData.value["formDefinitionId"] = null;
      targetData.value["userAccountId"] = null;
      users.value = [];
      forms.value = [];
      getLookups(`site/userSites/userAndRole?paramSiteIds=${val}`).then(
        (usersData: any) => {
          users.value = [];
          usersData.map((user) => {
            users.value.push({
              value: parseInt(user.userRoleId),
              label: user.userRoleName,
              type: user.type,
            });
          });
        }
      );
    };

    const userUpdated = (val) => {
      forms.value = [];
      targetData.value["formDefinitionId"] = "";
      getLookups(
        `site/filter/SCHEDULE_USER_SITE_FORMS?siteIds=${selectedSite.value}&userId=${val}`
      )
        .then((formsData: any) => {
          const newForms = formsData.map((form) => ({
            value: parseInt(form.value),
            label: form.text,
          }));

          forms.value = newForms;

          store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
        })
        .catch((err) => {
          console.error(err);
        });
    };

    const editConversation = (conv, index) => {
      targetData.value["conversationName"] = conv.title;
      selectedConversation.value = conv;
      conv.participants.map((participant) => {
        participantsIds.value.push(participant.webId.toString());
      });
      addConvDialog.value = true;
      const selectedParticipants = conv.participants.map(
        (participant) => !participant.deleted && participant.webId.toString()
      );
      formData.value.data = {
        title: conv.title,
        participants: selectedParticipants,
        index: index,
      } as any;
      formData.value.model[1].options = participantsOptions;
      formData.value.edit = true;
      formData.value.label = [{ editHeader: "Edit Chat" }];
    };

    const onTaskDelete = (data) => {
      task.value = forms.value.find(
        (form) => form.value == data.formDefinitionId
      );
      data.documentName = task.value.label;
      emit("task-delete", data);
    };

    const openConversationOverlay = () => {
      //TODO
    };

    const discard = () => {
      emit("update:modelValue", false);
      conversationsArray.value = [];
      conversations.value = [];
      linkedConversationsArray.value = [];
      targetData.value = {};
      users.value = [];
      forms.value = [];
      scheduleFormRef?.value?.resetForm();
      hideModal(scheduleModalRef.value as HTMLElement);
      nextTick(() => {
        scheduleFormRef?.value?.resetForm();
      });
    };

    const hideDialog = () => {
      emit("update:modelValue", false);
      closeButtonRef.value?.click();
      users.value = [];
      forms.value = [];
      conversations.value = [];
      conversationsArray.value = [];
      linkedConversationsArray.value = [];
      targetData.value = {};
      scheduleFormRef?.value?.resetForm();
    };

    const showNoteDetails = (note) => {
      selectedNote.value = note;
      showNotesDetail.value = true;
    };

    const saveNote = async () => {
      if (noteData?.value?.trim() == "") {
        return;
      }
      notes.value.push({
        date: FormatMethods.DateAndTime(moment()),
        user: currentUser.value.firstName + " " + currentUser.value.lastName,
        text: noteData.value,
        status: "ACTIVE",
        entityWebId: documentId.value == undefined ? null : documentId.value,
        entityTableName: "DOCUMENT",
        type: "TEXT",
        deleted: false,
        userId: currentUser.value.webId,
        noteId: null,
      });
      noteCloseButtonRef.value?.click();
      await clearNote();
    };

    const clearNote = async () => {
      noteFormRef.value?.resetForm({
        values: { note: "" },
        errors: {},
        touched: {},
      });
      noteData.value = "";
      await nextTick();
    };

    const openLinkConversationDialog = () => {
      linkConvDialog.value = true;
    };

    const openConversationDialog = () => {
      // formData.value.model[2].value = formName.value;
      formData.value.label = [{ addHeader: "Add Chat" }];
      formData.value.data = {};
      formData.value.edit = false;
      addConvDialog.value = true;
    };

    const submitScheduleData = async () => {
      task.value = forms.value.find(
        (form) => form.value == targetData.value.formDefinitionId
      );

      const startDate = targetData.value["startDate"];
      const endDate = targetData.value["endDate"];
      const diff = moment(endDate).diff(moment(startDate), "minutes");
      if (diff < 29) {
        toast.error(lessThanThirtyMints);
        return;
      }

      targetData.value.status = "SCHEDULE";
      targetData.value.documentName = task.value.label ?? "";
      targetData.value.type = null;
      targetData.value.fileId = null;
      targetData.value.flow = null;
      targetData.value.roleId = users.value.find(
        (user) =>
          parseInt(user.value) == parseInt(targetData.value["userAccountId"]) &&
          user.type == "ROLE"
      )
        ? parseInt(targetData.value["userAccountId"])
        : null;
      targetData.value.latitude = null;
      targetData.value.longitude = null;
      targetData.value.timeFrame = null;
      targetData.value.formScheduleId = null;
      targetData.value.completedDate = null;
      targetData.value.notes = notes.value.filter(
        (note) => note.noteId == null
      );
      targetData.value["startDate"] = moment(
        targetData.value["startDate"]
      ).format("YYYY-MM-DDTHH:mm:ss");
      targetData.value["endDate"] = moment(targetData.value["endDate"]).format(
        "YYYY-MM-DDTHH:mm:ss"
      );
      const selectedSite = sites.value.find(
        (site) => site.webId == targetData.value.siteId
      );
      if (editing.value == true) {
        conversationsArray.value = conversationsArray.value.map(
          (conversation) => {
            const { localId, ...rest } = conversation;
            return rest;
          }
        );
        targetData.value.conversationModel = conversationsArray.value;
        targetData.value.conversationLinked = linkedConversationsArray.value;
        emit("update", { ...targetData.value, selectedSite });
        conversations.value = [];
        conversationsArray.value = [];
        linkedConversationsArray.value = [];
      } else {
        conversationsArray.value = conversationsArray.value.map(
          (conversation) => {
            const { localId, ...rest } = conversation;
            return rest;
          }
        );
        targetData.value.conversationModel = conversationsArray.value;
        targetData.value.conversationLinked = linkedConversationsArray.value;
        emit("submit", { ...targetData.value, selectedSite });
        conversations.value = [];
        conversationsArray.value = [];
        linkedConversationsArray.value = [];
      }

      // Only close modal and cleanup after successful submission
      discard();
      getLikedConversations();
      deleteLinkedTask();
      showDialog.value = false;
    };

    const submitLinkConversationData = async (reqData) => {
      const itemExists = conversations.value.find(
        (item) => item.webId === reqData.conversationId
      );
      if (itemExists) {
        toast.error(conversationAlreadyExist);
        return;
      }
      const foundObject = linkConversationsList.value.find(
        (item) => item.webId === reqData.conversationId
      );
      foundObject.linked = true;
      if (!scheduleData.value.documentId) {
        linkedConversationsArray.value.push({
          conversationId: reqData.conversationId,
        });
      } else {
        linkedConversationsArray.value.push({
          conversationId: reqData.conversationId,
          documentId: scheduleData.value.documentId,
        });
      }
      // conversationsArray.value.push(foundObject);
      conversations.value.push(foundObject);
      linkConvDialog.value = false;
    };

    const submitConversationData = async (reqData) => {
      let conversationData = {} as any;
      if (formData.value.edit == true) {
        let filteredParticipants = [] as any;
        conversationData = { ...selectedConversation.value };
        conversationData.title = reqData.title;
        if (conversationData.webId) {
          let allParticipants = [
            ...conversations.value[reqData.index].participants.map(
              (item) => item.webId
            ),
          ];
          allParticipants = [...allParticipants, ...reqData.participants];
          const uniqueArray = allParticipants.filter((value, index, self) => {
            const stringValue = String(value);
            return self.map(String).indexOf(stringValue) === index;
          });
          filteredParticipants = participantsOptions.value.filter(
            (participant) => {
              return (
                uniqueArray
                  .map((value) => value.toString())
                  .includes(participant.value.toString()) === true
              );
            }
          );
        }
        if (conversationData.webId) {
          conversationData.participants = filteredParticipants.map(
            (partsId) => {
              if (reqData.participants.includes(partsId.value.toString())) {
                return {
                  webId: partsId.value.toString(),
                  deleted: false,
                  firstName: partsId.label,
                };
              } else {
                return {
                  webId: partsId.value.toString(),
                  deleted: true,
                  firstName: partsId.label,
                };
              }
            }
          );
        } else {
          conversationData.participants = participantsOptions.value.map(
            (partsId) => {
              if (reqData.participants.includes(partsId.value.toString())) {
                return {
                  webId: partsId.value.toString(),
                  deleted: false,
                  firstName: partsId.label,
                };
              } else {
                return {
                  webId: partsId.value.toString(),
                  deleted: true,
                  firstName: partsId.label,
                };
              }
            }
          );
        }
        if (!conversationData.webId) {
          conversationData.participants = conversationData.participants.filter(
            (participant) => !participant.deleted
          );
        }
        conversations.value[reqData.index] = conversationData;
        const existingIndex = conversationsArray.value.findIndex(
          (existingConversation) =>
            existingConversation.localId ===
            conversations.value[reqData.index].localId
        );
        if (existingIndex === -1) {
          conversationsArray.value.push(conversationData);
        } else {
          conversationsArray.value[existingIndex] = conversationData;
        }
        addConvDialog.value = false;
      } else {
        conversationData.title = reqData.title;
        conversationData.status = "OPEN";
        conversationData.participants = participantsOptions.value.map(
          (partsId) => {
            if (reqData.participants.includes(partsId.value.toString())) {
              return {
                webId: partsId.value.toString(),
                deleted: false,
                firstName: partsId.label,
              };
            } else {
              return {
                webId: partsId.value.toString(),
                deleted: true,
                firstName: partsId.label,
              };
            }
          }
        );
        conversationData.participants = conversationData.participants.filter(
          (participant) => !participant.deleted
        );
        conversationData.localId = Math.floor(Math.random() * 99999) + 1;
        if (
          !conversationData.participants.some(
            (participant) => participant.webId == currentUser.value.webId
          )
        ) {
          conversationData.participants.push({
            webId: currentUser.value.webId,
            deleted: false,
            firstName:
              currentUser.value.firstName + " " + currentUser.value.lastName,
          });
        }
        conversations.value.push(conversationData);
        conversationsArray.value.push(conversationData);
        addConvDialog.value = false;
      }
    };
    const populateForm = (data) => {
      targetData.value["siteId"] = null;
      targetData.value["siteId"] = data.siteId ? data.siteId : "";
      if (data.siteId) {
        siteUpdated(data.siteId);

        if (
          computed(() => {
            return assignType.value;
          }).value == "EVENT_ROLE"
        ) {
          forms.value = [];
          userUpdated(
            documentAssignmentModels.value.filter(
              (assignment) => assignment.userId == data.assignUserId
            )[0].roleId
          );
        } else {
          forms.value = [];
          userUpdated(data.assignUserId);
        }
      }
      if (modalTitle.value == translate("common.editTask")) {
        if (assignType.value == "EVENT_ROLE") {
          targetData.value["userAccountId"] = parseInt(
            documentAssignmentModels.value.filter(
              (assignment) => assignment.userId == data.assignUserId
            )[0].roleId
          );
          targetData.value["formDefinitionId"] = data.formDefinitionId
            ? parseInt(data.formDefinitionId)
            : "";
          targetData.value.assignUserId = data.assignUserId;
        } else {
          if (
            documentAssignmentModels.value &&
            documentAssignmentModels.value[0].roleId
          ) {
            targetData.value["userAccountId"] =
              documentAssignmentModels.value[0].roleId;
          } else {
            targetData.value["userAccountId"] = data.assignUserId
              ? parseInt(data.assignUserId)
              : "";
          }
          targetData.value["formDefinitionId"] = data.formDefinitionId
            ? parseInt(data.formDefinitionId)
            : "";
        }
      } else {
        siteUpdated(data.siteId);
      }
      task.value = forms.value.filter(
        (form) => form.webId == data.formDefinitionId
      )[0]?.name;
      targetData.value["startDate"] = data.start;
      targetData.value["endDate"] = data.end;
      targetData.value["userAccountId"] = data.roleId
        ? parseInt(data.roleId)
        : data.assignUserId
        ? parseInt(data.assignUserId)
        : "";
      if (data.id != null) {
        targetData.value["webId"] = data.id;
      }
    };

    const getUserList = async () => {
      userList.value = await UserAPI.getInstance().getUserList({
        size: 100000,
      });
    };

    const setFormValue = async (newValue) => {
      conversations.value = [];
      // store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
      scheduleFormRef?.value?.resetForm();
      let user;

      if (newValue?.userId) {
        user = userList.value.content.find((user) => {
          return String(user.webId) === String(newValue.userId);
        });
      } else if (newValue?.assignUserId) {
        user = userList.value.content.find((user) => {
          return String(user.webId) === String(newValue.assignUserId);
        });
      }

      if (user) {
        targetData.value["pnUuid"] = user.pnUuid;
      }

      if (newValue.documentId) {
        getLookups(`/document/${newValue.documentId}`).then((data: any) => {
          notes.value =
            data.notes == null
              ? []
              : data.notes.map((data) => {
                  return {
                    date: FormatMethods.DateAndTime(data.createdDate),
                    user: data.createdBy,
                    text: data.text,
                    status: "ACTIVE",
                    entityWebId: data.entityWebId,
                    entityTableName: data.entityTableName,
                    type: "TEXT",
                    deleted: false,
                    userId: data.createdById,
                    noteId: data.webId,
                  };
                });
          initialNotes.value = { ...data.notes };
          if (!editable.value) {
            formDisabled.value = true;
          } else if (
            data.scheduleStatus != "SCHEDULE" ||
            data.status === "IN_PROGRESS"
          ) {
            formDisabled.value = true;
          } else if (data.documentType == "CRON") {
            formDisabled.value = true;
          } else if (data.assignType == "DIRECT_ASSIGNMENT") {
            formDisabled.value = false;
          }
          assignType.value = data.assignType;
          documentAssignmentModels.value = data.documentAssignmentModels;
          if (newValue.id !== null && newValue !== undefined) {
            targetData.value["status"] = data.status ? data.status : false;
            targetData.value["scheduleStatus"] = data.scheduleStatus
              ? data.scheduleStatus
              : false;
            populateForm(newValue);
          }
        });
        documentId.value = newValue.documentId;
        getConversations();
      }
      formName.value = newValue.formName;
      documentId.value = newValue.documentId;
      if (newValue.id != null) {
        editing.value = true;
        modalTitle.value = formDisabled.value
          ? translate("common.viewTask")
          : translate("common.editTask");
        populateForm(newValue);
        nextTick(() => {
          ///
        });
      } else if (newValue.id == undefined && newValue.resourceId != undefined) {
        editing.value = false;
        modalTitle.value = translate("common.addTask");
        populateForm(newValue);
        nextTick(() => {
          formDisabled.value = false;
          scheduleFormRef?.value?.resetForm();
        });
      } else {
        // targetData.value = {};
        editing.value = false;
        modalTitle.value = translate("common.addTask");
        scheduleFormRef?.value?.resetForm();
        nextTick(() => {
          formDisabled.value = false;
          scheduleFormRef?.value?.resetForm();
        });
      }
    };
    const getConversations = () => {
      if (documentId.value !== undefined) {
        getLookups(
          `notification/conversations?status=OPEN&entityWebId=${documentId.value}`
        )
          .then((convData: any) => {
            conversations.value = convData.content;
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    const disablePreviousDates = (time) => {
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0); // Set time to midnight

      return time.getTime() < currentDate.getTime();
    };

    watch(
      modelValueProp,
      async (newValue) => {
        scheduleFormRef?.value?.resetForm();
        notes.value = [];
        if (detailTab.value && noteTab.value && conversationTab.value) {
          detailTab.value.className = "nav-link";
          noteTab.value.className = "nav-link";
          conversationTab.value.className = "nav-link";
          var clickEvent = new Event("click");
          detailTab.value.dispatchEvent(clickEvent);
        }
        // targetData.value["userAccountId"] = "";
        if (!newValue) {
          targetData.value = {};
          conversations.value = [];
          conversationsArray.value = [];
          linkedConversationsArray.value = [];
          scheduleFormRef?.value?.resetForm();
        }
        if (newValue) {
          nextTick(() => {
            formDisabled.value = false;
            scheduleFormRef?.value?.resetForm();
          });
          formDisabled.value = false;
          scheduleFormRef?.value?.resetForm();
          await getUserList();
          setFormValue(scheduleData.value);
        }
        showDialog.value = newValue;
      },
      {
        deep: true,
      }
    );
    return {
      disablePreviousDates,
      scheduleFormRef,
      loading,
      scheduleModalRef,
      targetData,
      scheduleFormData,
      submitScheduleData,
      scheduleData,
      modalTitle,
      translate,
      sites,
      forms,
      users,
      showDialog,
      hideDialog,
      displayDateTime,
      formDisabled,
      notes,
      conversationsArray,
      noteFormRef,
      noteData,
      saveNote,
      deleteNote,
      clearNote,
      noteValidationSchema,
      noteCloseButtonRef,
      showNotesDetail,
      selectedNote,
      showNoteDetails,
      discard,
      onTaskDelete,
      participantsOptions,
      detailTab,
      noteTab,
      userUpdated,
      participants,
      submitConversationData,
      submitLinkConversationData,
      conversations,
      linkConvDialog,
      addConvDialog,
      openConversationDialog,
      openLinkConversationDialog,
      task,
      linkFormData,
      deleteConversation,
      siteUpdated,
      editConversation,
      hasPermission,
      showDeleteDialog,
      participantsIds,
      openConversationOverlay,
      handleDelete,
      documentId,
      formData,
      toggleChat,
      FormatMethods,
      conversationTab,
      t,
      linkConversationsList,
      getLikedConversations,
      conversationsToDelete,
      deleteLinkedTask,
      linkedConversationsArray,
    };
  },
});
